import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { DataTable } from 'primereact/datatable';
import { Row } from 'primereact/row';
import { useTranslation } from 'react-i18next';

import { EmissionValueWithSuffix } from '../../../../../../../../../../../shared/components/emission-value-with-suffix/EmissionValueWithSuffix';
import { emissionValueWithDecimals } from '../../../../../../../../../../../shared/helpers/emission-value-with-decimals';
import {
  DeclaredUnit,
  DeclaredUnitToPlainSymbolsMap,
} from '../../../../../../../../../../../shared/interfaces/IComponentDetails';
import { EmissionSymbol } from '../../../../../../../../../../../shared/interfaces/IFootprint';
import { IInputMaterial } from '../../../PcfWizard.type';
import { getMaterialsTableRows, getMaterialsTotalEmissions, MaterialsTableRow } from '../helpers';
import TableHeaderWithUnit from './TableHeaderWithUnit';

type MaterialsAndWasteTreatmentTableProps = {
  materials?: IInputMaterial[];
};

type EmissionFactor = MaterialsTableRow['emissionFactor'];

const KILOGRAM_SYMBOL = DeclaredUnitToPlainSymbolsMap[DeclaredUnit.kilogram];

// The factor column header reads "kg CO₂e / kg", which is what most rows are in.
const EMISSION_FACTOR_HEADER_UNIT = `${EmissionSymbol.KG_CO2E} / ${KILOGRAM_SYMBOL}`;

// Shows the factor with its own denominator, e.g. "kg CO₂e / 4 piece". Used for declared factors.
const renderDeclaredEmissionFactor = (factor: EmissionFactor): JSX.Element => {
  return (
    <EmissionValueWithSuffix
      emissionValue={factor.value}
      declaredUnit={factor.declaredUnit}
      unitaryProductAmount={factor.perAmount}
      emissionSymbol={EmissionSymbol.KG_CO2E}
      abbreviateUnit={true}
      emissionValueDecimals={6}
      numericalValueTextClassName='font-normal'
      suffixTextClassName='font-normal'
    />
  );
};

/**
 * Input materials of an Individual Process, each directly followed by its own waste treatment.
 */
const MaterialsAndWasteTreatmentTable = ({ materials }: MaterialsAndWasteTreatmentTableProps) => {
  const { t } = useTranslation();

  const rows = getMaterialsTableRows(materials);

  if (rows.length === 0) {
    return null;
  }

  const nameTemplate = (row: MaterialsTableRow) => {
    if (!row.isWaste) {
      return row.name;
    }

    return (
      <span className='pl-3 text-color-secondary'>
        <span aria-hidden='true' className='mr-2'>
          ↳
        </span>
        <span>{row.name}</span>
      </span>
    );
  };

  // Amounts are always in kg.
  const amountTemplate = (row: MaterialsTableRow) => {
    return row.amount == null ? '' : `${emissionValueWithDecimals(row.amount)} ${KILOGRAM_SYMBOL}`;
  };

  const emissionFactorTemplate = (row: MaterialsTableRow) => {
    return (
      <div className='flex flex-column'>
        {/* Always per kg, which is already in the header. */}
        <span>
          {row.emissionFactor.value == null
            ? ''
            : emissionValueWithDecimals(row.emissionFactor.value)}
        </span>

        {row.declaredEmissionFactor && (
          <div className='flex text-xs text-color-secondary'>
            <span>(</span>
            {renderDeclaredEmissionFactor(row.declaredEmissionFactor)}
            <span>)</span>
          </div>
        )}
      </div>
    );
  };

  const sourceTemplate = (row: MaterialsTableRow) => {
    return <span className='text-color-secondary'>{row.source}</span>;
  };

  const emissionsTemplate = (row: MaterialsTableRow) => {
    return row.emissions == null ? '' : emissionValueWithDecimals(row.emissions);
  };

  const totalEmissions = getMaterialsTotalEmissions(rows);

  // The label spans every column but the last, so the total sits under the emissions it sums. The
  // unit is already in the last column's header.
  const footerColumnGroup =
    totalEmissions == null ? undefined : (
      <ColumnGroup>
        <Row>
          <Column
            footer={t(
              'calculationDetailsPage.sections.individualProcess.table.materialsAndWasteTotal',
            )}
            colSpan={4}
            footerClassName='font-semibold'
          />
          <Column
            footer={emissionValueWithDecimals(totalEmissions)}
            footerClassName='font-semibold'
            footerStyle={{ textAlign: 'right' }}
          />
        </Row>
      </ColumnGroup>
    );

  return (
    <DataTable
      value={rows}
      dataKey='key'
      size='small'
      className='text-sm border-round-xl overflow-hidden'
      tableStyle={{ tableLayout: 'fixed', wordBreak: 'break-word' }}
      footerColumnGroup={footerColumnGroup}
    >
      <Column
        header={t(
          'calculationDetailsPage.sections.individualProcess.table.materialsAndWasteTreatment',
        )}
        body={nameTemplate}
        style={{ width: '32%' }}
      />
      <Column
        header={t('calculationDetailsPage.sections.individualProcess.table.amount')}
        body={amountTemplate}
        align='right'
        alignHeader='right'
        style={{ width: '12%' }}
      />
      <Column
        header={
          <TableHeaderWithUnit
            label={t('calculationDetailsPage.sections.individualProcess.table.emissionFactor')}
            unit={EMISSION_FACTOR_HEADER_UNIT}
          />
        }
        body={emissionFactorTemplate}
        style={{ width: '21%' }}
      />
      <Column
        header={t('calculationDetailsPage.sections.individualProcess.table.source')}
        body={sourceTemplate}
        style={{ width: '20%' }}
      />
      <Column
        header={
          <TableHeaderWithUnit
            label={t('calculationDetailsPage.sections.individualProcess.table.emissions')}
            unit={EmissionSymbol.KG_CO2E}
            alignEnd
          />
        }
        body={emissionsTemplate}
        align='right'
        alignHeader='right'
        style={{ width: '15%' }}
      />
    </DataTable>
  );
};

export default MaterialsAndWasteTreatmentTable;
